import React, { useEffect, useRef, useState } from "react";
import "./Checkout.scss";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

import { AiFillThunderbolt } from "react-icons/ai";
import { IoGridSharp, IoSearch } from "react-icons/io5";
import { MdAddBox, MdOutlineQrCodeScanner } from "react-icons/md";
import {
  FaCaretUp,
  FaChevronDown,
  FaList,
  FaLongArrowAltDown,
  FaMinus,
} from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { MdOutlineRemoveShoppingCart } from "react-icons/md";

import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import ProductItem from "../Product/ManageProduct/ProductItem";

import ROUTES from "../../constants/routes";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import InputField from "../../components/InputField/InputField";
import { toast } from "react-toastify";
import {
  addItemToCart,
  clearCart,
  removeItemFromCart,
  updateTaxRate,
} from "../../redux/features/cart-slice";
import Loader from "../../components/Loader/Loader";
import {
  loadNextProducts,
  loadProductsIfNeeded,
  updateLastParams,
  updateListStyle,
} from "../../redux/features/products-slice";
import Cart from "./Cart/Cart";
import { Capacitor } from "@capacitor/core";
import useUserIsAllowed from "../../helpers/userAuthorized";
import { storage } from "../../helpers/storage";
import SyncLoader from "../../components/SyncLoader/SyncLoader";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useClickOutside } from "use-events";
import { LuSearchX } from "react-icons/lu";
import { updateNetworkStatus } from "../../redux/features/home-slice";
import { IoIosArrowRoundUp } from "react-icons/io";

const Checkout = () => {
  const {
    id: organization_id,
    currency,
    categories,
    tax_rate,
  } = useSelector((state) => state.organizationReducer.value);

  const { id: branch_id } = useSelector((state) => state.branchReducer.value);

  const { items, subTotalPrice, totalItems } = useSelector(
    (state) => state.cartReducer.value
  );

  const { run } = useSelector((state) => state.tourReducer.value);
  const { tour_checkout } = useSelector((state) => state.userReducer.value);

  const {
    products,
    lastParams,
    loading: loadingProducts,
    loadingNextPage,
    currentPage,
    lastPage,
    listStyle,
    loadingBackground,
    loadBackgroundFailed,
  } = useSelector((state) => state.productReducer.value);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(updateTaxRate({ tax_rate: tax_rate }));
  }, [tax_rate]);

  const cartRef = useRef(null);

  const [openScanner, setOpenScanner] = useState(false);

  const [selectOptions, setSelectOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState({});
  const [serachKeyword, setSearchKeyword] = useState(lastParams?.keyword || "");
  const [debounceSerachKeyword, setDebounceSearchKeyword] = useState(
    lastParams?.keyword || ""
  );

  const [openSortOptions, setOpenSortOptions] = useState(false);
  const [sortBy, setSortBy] = useState(lastParams?.sortBy || "created_at");
  const [sort, setSort] = useState(lastParams?.sort || "desc");

  const sortOptions = [
    {
      label: "Date",
      value: "created_at",
    },
    {
      label: "Price",
      value: "price",
    },
    {
      label: "A-Z",
      labelSecond: "Z-A",
      value: "name",
    },
    {
      label: "Stock",
      value: "current_stock",
    },
  ];

  const [selectedTab, setSelectedTab] = useState("");

  const [quantityMultiplier, setQuantityMultiplier] = useState(1);

  const [pageNumber, setPageNumber] = useState(currentPage || 1);
  const lastProductRef = useRef(null);

  const userIsAllowedToCreateSales = useUserIsAllowed("add_orders_or_sales");

  useEffect(() => {
    return () => {
      dispatch(updateLastParams());
    };
  }, [dispatch]);

  const [productNotFound, setProductNotFound] = useState(null);

  useEffect(() => {
    if (debounceSerachKeyword && products?.length < 1) {
      setProductNotFound(`No product found for ${debounceSerachKeyword}`);
    }
  }, [debounceSerachKeyword, products]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          pageNumber < lastPage &&
          !loadingNextPage &&
          !loadingProducts
        ) {
          setPageNumber((prevPage) => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    const currentRef = lastProductRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    pageNumber,
    lastPage,
    loadingNextPage,
    loadingProducts,
    lastProductRef.current,
    products,
    debounceSerachKeyword,
    sort,
    sortBy,
  ]);

  const getCategoriesOptions = () => {
    let optionsData = [
      {
        label: "All Products",
        value: "",
      },
      {
        label: "Highlighted Products",
        value: "highlighted",
      },
    ];
    categories?.map((item) => {
      const option = {
        label: `${item?.name}`,
        value: item?.id,
      };
      optionsData.push(option);
    });

    setSelectOptions(optionsData);
  };

  useEffect(() => {
    if (!selectedOption?.label) {
      const newSelectedOption = selectOptions?.find(
        (el) => el?.value === lastParams?.category
      );
      if (newSelectedOption) {
        setSelectedOption(newSelectedOption);
      } else {
        if (selectOptions?.length > 0) {
          setSelectedOption(selectOptions[0]);
        }
      }
    }
  }, [lastParams, selectOptions]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceSearchKeyword(serachKeyword);
    }, 500);

    return () => clearTimeout(handler);
  }, [serachKeyword]);

  useEffect(() => {
    if (selectedOption?.label && !loadingProducts) {
      dispatch(
        loadProductsIfNeeded({
          organization_id: organization_id,
          branch_id: branch_id,
          keyword: debounceSerachKeyword,
          category: selectedOption?.value || "",
          sort: sort,
          sortBy: sortBy,
        })
      );
      setPageNumber(1);
    }
  }, [
    organization_id,
    branch_id,
    debounceSerachKeyword,
    selectedOption,
    sort,
    sortBy,
  ]);

  useEffect(() => {
    getCategoriesOptions();
  }, [categories]);

  const handleAddToCart = async (item, quantity, nonInventory) => {
    if (userIsAllowedToCreateSales) {
      let itemData = {};
      if (nonInventory) {
        itemData = {
          newItem: {
            product: item,
            quantity: quantity,
            nonInventory: nonInventory,
          },
        };
      } else {
        itemData = {
          newItem: {
            product: item,
            quantity: quantityMultiplier,
          },
        };
      }
      let prev_items = await storage.get("cart");
      prev_items = prev_items ? JSON.parse(prev_items) : [];

      const exists = prev_items?.findIndex(
        (el) => el?.newItem?.product?.id === itemData?.newItem?.product?.id
      );

      if (exists !== -1) {
        if (!item?.nonInventory) {
          prev_items[exists].newItem.quantity += 1;
        } else {
          prev_items[exists].newItem.product.quantity += 1;
        }
      } else {
        prev_items = [...prev_items, itemData];
      }

      storage.set("cart", JSON.stringify(prev_items));

      dispatch(addItemToCart(itemData));
    } else {
      toast.dismiss();
      toast.info(
        "You do not have sufficient permissions to perform this action. Please contact your administrator."
      );
    }
  };

  const handleCheckout = () => {
    if (items?.length > 0) {
      navigate(ROUTES.cart.path);
    }
  };

  const handleClearCart = () => {
    dispatch(clearCart());
    storage.remove("cart");
  };

  useEffect(() => {
    const handleCartStorage = async () => {
      if (items?.length === 0 && branch_id) {
        let cart_items = await storage.get("cart");
        cart_items = cart_items ? JSON.parse(cart_items) : [];

        if (cart_items?.length > 0) {
          cart_items?.forEach((el) => {
            const exists = el?.newItem?.product?.branch_stocks?.find(
              (item) => item?.branch_id === branch_id
            );

            if (exists || el?.nonInventory) {
              dispatch(addItemToCart(el));
            } else {
              const new_cart = cart_items?.filter((item) => item !== el);
              storage.set("cart", JSON.stringify(new_cart));
            }
          });
        }
      } else if (branch_id && items?.length > 0) {
        const tempProducts = [...items];

        tempProducts?.map((el) => {
          const exists = el?.product?.branch_stocks?.find(
            (item) => item?.branch_id === branch_id
          );

          if (!exists && !el?.newItem?.nonInventory) {
            dispatch(
              removeItemFromCart({ newItem: { ...el, removeAll: true } })
            );
          }
        });
      }
    };

    handleCartStorage();
  }, [branch_id]);

  useEffect(() => {
    if (lastParams?.organization_id && lastParams?.branch_id) {
      if (
        lastParams?.organization_id !== organization_id &&
        lastParams?.branch_id !== branch_id
      ) {
        storage.remove("cart");
        dispatch(clearCart());
      }
    }
  }, [lastParams, organization_id, branch_id]);

  useEffect(() => {
    if (pageNumber > currentPage) {
      dispatch(loadNextProducts({ page: pageNumber }));
    }
  }, [pageNumber]);

  const handleSort = (el) => {
    if (sortBy === el?.value) {
      if (sort === "asc") {
        setSort("desc");
      } else {
        setSort("asc");
      }
    } else {
      setSortBy(el?.value);
    }
    // setOpenSortOptions(false);
  };

  const sortRef = useRef(null);

  useClickOutside([sortRef], () => {
    setOpenSortOptions(false);
  });

  useEffect(() => {
    if (loadingBackground) {
      dispatch(updateNetworkStatus({ status: "syncing" }));
    }
  }, [loadingBackground]);

  return (
    <>
      {loadingProducts && <Loader />}
      <div className="page-with-side-components">
        <div className="checkout">
          <div className="desktop-navbar">
            <p>{ROUTES.checkout.title}</p>
            <div className="last-tag-svg">
              {items?.length > 0 && (
                <MdOutlineRemoveShoppingCart
                  className="danger"
                  onClick={handleClearCart}
                />
              )}
              {/* <MdOutlineQrCodeScanner
                onClick={() => {
                  setOpenScanner((pre) => !pre);
                }}
              /> */}
              <p className="sort">
                Sort by {sortOptions?.find((el) => el.value === sortBy)?.label}
                <FaCaretUp
                  className={sort === "desc" ? "opposite" : ""}
                />
              </p>
              <BsThreeDotsVertical
                onClick={() => {
                  setOpenSortOptions((pre) => !pre);
                }}
              />
            </div>
          </div>

          <div
            className={`sort-by-options ${openSortOptions ? "active" : ""}`}
            ref={sortRef}
          >
            {sortOptions?.map((el, i) => {
              return (
                <p
                  className={sortBy === el.value ? "active" : ""}
                  onClick={() => {
                    handleSort(el);
                  }}
                >
                  {el.value === "name" && sort === "desc"
                    ? el?.labelSecond
                    : el.label}

                  <FaCaretUp
                    className={sort === "desc" ? "rotate" : ""}
                  />
                </p>
              );
            })}
          </div>

          <div className="topbar customPerPageTopBar">
            <div className="left">
              <CustomSelect
                options={selectOptions}
                value={selectedOption}
                register={() => {}}
                required={true}
                name="isHighlight"
                handleChange={(opt) => {
                  setSelectedOption(opt);
                }}
                reset={() => {}}
              />
            </div>

            <div className="middle">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setDebounceSearchKeyword(e.target.keyword.value);
                }}
                className="inputs"
              >
                <input
                  type="text"
                  name="keyword"
                  defaultValue={serachKeyword}
                  placeholder="Search."
                  onChange={(e) => {
                    setSearchKeyword(e.target.value);
                  }}
                />
                <button type="submit">
                  <IoSearch />
                </button>
              </form>
            </div>
            <div className="right">
              <p
                className="icon mobile-search-icon"
                onClick={() => {
                  if (selectedTab !== "search") {
                    setSelectedTab("search");
                  } else {
                    setSelectedTab("");
                  }
                }}
              >
                <IoSearch />
              </p>

              <p
                className="icon"
                onClick={() => {
                  if (listStyle === "list") {
                    dispatch(updateListStyle({ listStyle: "grid" }));
                  } else {
                    dispatch(updateListStyle({ listStyle: "list" }));
                  }
                  setSelectedTab("");
                }}
              >
                {listStyle !== "list" ? <FaList /> : <IoGridSharp />}
              </p>
              <p
                className="icon"
                onClick={() => {
                  if (userIsAllowedToCreateSales) {
                    setSelectedTab("nonInventory");
                  } else {
                    toast.dismiss();
                    toast.info(
                      "You do not have sufficient permissions to perform this action. Please contact your administrator."
                    );
                  }
                }}
              >
                <AiFillThunderbolt />
              </p>
              <p
                className="icon last-item"
                onClick={() => {
                  if (userIsAllowedToCreateSales) {
                    setSelectedTab("quantity");
                  } else {
                    toast.dismiss();
                    toast.info(
                      "You do not have sufficient permissions to perform this action. Please contact your administrator."
                    );
                  }
                }}
              >
                {quantityMultiplier}X
              </p>
            </div>
          </div>

          <div
            className={`search-bar ${selectedTab === "search" ? "active" : ""}`}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setDebounceSearchKeyword(e.target.keyword.value);
              }}
              className="inputs"
            >
              <input
                type="text"
                name="keyword"
                defaultValue={serachKeyword}
                placeholder="Search."
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                }}
              />
              <button type="submit">
                <IoSearch />
              </button>
            </form>
          </div>

          <div
            className={`overallProductList container-wrapper ${
              products.length > 0 && "flexStart"
            } ${
              selectedTab === "search" ? "searchActive checkout-products" : ""
            }`}
          >
            {products.length === 0 && (
              <div className="first-item">
                <Link className="mobile" to={ROUTES.addProduct.path}>
                  {productNotFound ? <LuSearchX /> : <MdAddBox />}
                </Link>
                <Link className="desktop" to={ROUTES.manageProduct.path}>
                  {productNotFound ? <LuSearchX /> : <MdAddBox />}
                </Link>
                <p>
                  {productNotFound ? (
                    productNotFound
                  ) : (
                    <>
                      Hey! Let's add your first product <br />
                      so you can start selling right away! <br /> <br />
                      If you want to sell without adding product <br />
                      tab the lightning icon on the top bar.
                    </>
                  )}
                </p>
              </div>
            )}
            {products.length > 0 && (
              <div className={`product-items ${listStyle}-style`}>
                {products.map((item, i) => {
                  let itemFound = items.find((el) => el.product.id === item.id);
                  const selected = itemFound ? true : false;
                  if (products?.length - 1 === i) {
                    return (
                      <div
                        className="load-next"
                        style={{ width: "100%" }}
                        ref={lastProductRef}
                        key={i}
                      >
                        <ProductItemWrapper
                          selected={selected}
                          currency={currency}
                          item={item}
                          handleAddToCart={handleAddToCart}
                          cartRef={cartRef}
                          userIsAllowedToCreateSales={
                            userIsAllowedToCreateSales
                          }
                        />
                      </div>
                    );
                  } else {
                    return (
                      <ProductItemWrapper
                        key={i}
                        selected={selected}
                        currency={currency}
                        item={item}
                        handleAddToCart={handleAddToCart}
                        cartRef={cartRef}
                        userIsAllowedToCreateSales={userIsAllowedToCreateSales}
                      />
                    );
                  }
                })}
                {loadingNextPage && (
                  <div className="product-loading-item">
                    <div className="image"></div>
                    <div className="content">
                      <div className="details">
                        <div className="name"></div>
                        <div className="stock"></div>
                      </div>
                      <div className="price"></div>
                    </div>
                  </div>
                )}
                <div
                  className="desktop-product-item"
                  onClick={() => {
                    navigate(ROUTES.manageProduct.path);
                  }}
                >
                  <FaPlus />
                </div>
              </div>
            )}
          </div>

          <div className="floating-button" ref={cartRef}>
            <CustomButton
              name={`Checkout${
                totalItems > 0 ? "(" + totalItems + ")" : ""
              } - ${currency}${subTotalPrice}`}
              type={"button"}
              color={"primary"}
              bigButton={true}
              handleSubmit={handleCheckout}
              disabled={items?.length <= 0 && !(run && tour_checkout)}
              id={"tour-checkout"}
            />
          </div>

          <AddNonInventoryItem
            handleAddToCart={handleAddToCart}
            openAddNonInventoryItem={selectedTab}
            setOpenAddNonInventoryItem={setSelectedTab}
          />

          <QuantityMultiplyModel
            open={selectedTab}
            setOpen={setSelectedTab}
            quantityMultiplier={quantityMultiplier}
            setQuantityMultiplier={setQuantityMultiplier}
          />

          <div className="open-scanner-icon">
            {items?.length > 0 && (
              <MdOutlineRemoveShoppingCart
                className="danger"
                onClick={handleClearCart}
              />
            )}
            {/* <MdOutlineQrCodeScanner
              onClick={() => {
                setOpenScanner((pre) => !pre);
              }}
            /> */}
            <p className="sort">
              Sort by {sortOptions?.find((el) => el.value === sortBy)?.label}
              <FaCaretUp
                className={sort === "desc" ? "opposite" : ""}
              />
            </p>
            <BsThreeDotsVertical
              onClick={() => {
                setOpenSortOptions((pre) => !pre);
              }}
            />
          </div>

          {selectedTab && selectedTab !== "search" && (
            <div
              className="blur-back"
              onClick={() => {
                setSelectedTab("");
              }}
            ></div>
          )}
        </div>

        <div className="right-desktop-view" id="tour-checkout-desktop">
          <div className="desktop-navbar box-shadow border-bottom">
            <p>{ROUTES.cart.title}</p>
          </div>
          {totalItems > 0 || run ? (
            <Cart desktopView={true} selectedTab={selectedTab} />
          ) : (
            <div className="no-cart-items">
              <img
                src="/images/empty-cart.png"
                alt=""
                aria-label="Empty Cart"
              />
              <p>
                There is no items selected in the cart. <br />
                Please select or click on the items to put it in the cart
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Checkout;

const ProductItemWrapper = ({
  item,
  handleAddToCart,
  selected,
  currency,
  cartRef,
  userIsAllowedToCreateSales,
}) => {
  const itemImageRef = useRef(null);

  const flyToCart = () => {
    if (userIsAllowedToCreateSales) {
      const productImg = itemImageRef.current;
      const cart = cartRef.current;

      if (!productImg || !cart) return;

      const productRect = productImg.getBoundingClientRect();
      const cartRect = cart.getBoundingClientRect();

      const flyingImg = productImg.cloneNode(true);
      flyingImg.className += " flying-cloned-product-image-item";
      document.body.appendChild(flyingImg);

      flyingImg.style.position = "absolute";
      flyingImg.style.top = `${productRect.top}px`;
      flyingImg.style.left = `${productRect.left}px`;
      flyingImg.style.width = `${productRect.width}px`;
      flyingImg.style.transition =
        "transform 1s ease-in-out, opacity 1s ease-in-out";
      flyingImg.style.zIndex = 1000;

      flyingImg.getBoundingClientRect();

      const cartCenterX = cartRect.left + cartRect.width / 2;
      const cartCenterY = cartRect.top + cartRect.height / 2;

      const translateX =
        cartCenterX - (productRect.left + productRect.width / 2);
      const translateY =
        cartCenterY - (productRect.top + productRect.height / 2);

      flyingImg.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.1)`;
      flyingImg.style.opacity = 0.3;

      setTimeout(() => {
        flyingImg.remove();
      }, 1000);
    }
  };

  return (
    <ProductItem
      item={item}
      currency={currency}
      selected={selected}
      checkout={true}
      imageRef={itemImageRef}
      handleClick={() => {
        flyToCart();
        handleAddToCart(item);
      }}
    />
  );
};

const AddNonInventoryItem = ({
  openAddNonInventoryItem,
  setOpenAddNonInventoryItem,
  handleAddToCart,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const [submitting, setSubmitting] = useState(false);
  const [nonInventoryCount, setNonInventoryCount] = useState(0);

  const handleAddNonInventoryItem = (data) => {
    setSubmitting(true);
    const itemData = {
      product: {
        id: `${data.name}${nonInventoryCount}`,
        name: data.name,
        price: data.price,
        description: data.description,
        quantity: Number(data.quantity),
      },
      quantity: Number(data.quantity),
      nonInventory: true,
    };
    handleAddToCart(itemData.product, itemData.quantity, itemData.nonInventory);
    setNonInventoryCount((pre) => pre + 1);
    clearForm();
    setOpenAddNonInventoryItem("");
    toast.dismiss();
    toast.success(`${itemData.product.name} added to the cart.`);
    setSubmitting(false);
  };

  const clearForm = () => {
    reset({
      name: "",
      price: null,
      description: "",
      quantity: null,
    });
  };

  const [shouldRender, setShouldRender] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (openAddNonInventoryItem === "nonInventory") {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else if (isVisible) {
      setIsVisible(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [openAddNonInventoryItem, isVisible]);

  if (!shouldRender) return null;

  return (
    <form
      disabled={submitting}
      onSubmit={handleSubmit(handleAddNonInventoryItem)}
      className={`add-non-inventory-item ${isVisible ? "active" : ""}`}
    >
      <h1 className="heading">Add Non-Inventory Item</h1>

      <div className="form-content">
        <InputField
          name={"name"}
          type={"text"}
          label={"Name"}
          required={true}
          register={register}
          inputErrors={errors}
          watch={watch}
        />

        <InputField
          name={"price"}
          type={"number"}
          label={"Price"}
          required={true}
          register={register}
          inputErrors={errors}
          watch={watch}
          constraints={{
            min: 0.01,
          }}
        />

        <InputField
          name={"description"}
          type={"text"}
          label={"Description"}
          required={false}
          register={register}
          inputErrors={errors}
          watch={watch}
        />

        <InputField
          name={"quantity"}
          type={"number"}
          label={"Quantity"}
          required={true}
          register={register}
          inputErrors={errors}
          watch={watch}
          constraints={{
            min: 1,
          }}
        />
      </div>

      <div className="buttons">
        <CustomButton
          name={"Cancel"}
          color={"danger"}
          type={"button"}
          handleSubmit={() => {
            setOpenAddNonInventoryItem("");
            clearForm();
          }}
          disabled={submitting}
        />

        <CustomButton
          name={"Add To Cart"}
          color={"primary"}
          type={"submit"}
          handleSubmit={() => {}}
          disabled={submitting}
          loader={submitting}
        />
      </div>
    </form>
  );
};

const QuantityMultiplyModel = ({
  open,
  setOpen,
  quantityMultiplier,
  setQuantityMultiplier,
}) => {
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm();
  const [tempMultiplier, setTempMultiplier] = useState(quantityMultiplier);

  useEffect(() => {
    if (open) {
      setTempMultiplier(quantityMultiplier);
    }
  }, [open]);

  const handleChangeMultiplier = (data) => {
    setQuantityMultiplier(Number(tempMultiplier));
    setOpen("");
  };

  const [shouldRender, setShouldRender] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open === "quantity") {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else if (isVisible) {
      setIsVisible(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [open, isVisible]);

  if (!shouldRender) return null;

  return (
    <form
      onSubmit={handleSubmit(handleChangeMultiplier)}
      className={`multiply-modal ${isVisible ? "active" : ""}`}
    >
      <p className="heading-left">Select Quantity</p>
      <div className="inputs-container">
        <div className="add-sub">
          <div
            className={`icon ${tempMultiplier > 1 ? "active" : ""}`}
            onClick={() => {
              if (tempMultiplier > 1) {
                setTempMultiplier((pre) => Number(pre) - 1);
              }
            }}
          >
            <FaMinus />
          </div>
          <p>{tempMultiplier}</p>
          <div
            className={`icon ${true ? "active" : ""}`}
            onClick={() => {
              setTempMultiplier((pre) => Number(pre) + 1);
            }}
          >
            <FaPlus />
          </div>
        </div>

        <InputField
          name={"quantity"}
          type={"number"}
          label={"Enter Quantity"}
          required={true}
          register={register}
          inputErrors={errors}
          watch={watch}
          value={tempMultiplier}
          handleChange={(data) => {
            setTempMultiplier(Number(data).toFixed(0));
          }}
          constraints={{
            min: 1,
            max: 99,
          }}
        />
      </div>

      <div className="buttons">
        <CustomButton
          name={"Cancel"}
          color={"danger"}
          type={"button"}
          handleSubmit={() => {
            setOpen("");
            setTempMultiplier(quantityMultiplier);
          }}
        />

        <CustomButton
          name={"OK"}
          color={"primary"}
          type={"submit"}
          handleSubmit={() => {}}
        />
      </div>
    </form>
  );
};
